import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, Lock, Eye, FileText, Server, Users, CheckCircle, Award } from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";

const PrivacyPolicy = () => {
  const securityMeasures = [
    {
      icon: Shield,
      title: "Advanced Encryption",
      description: "All client data is encrypted using industry-standard AES-256 encryption both in transit and at rest."
    },
    {
      icon: Lock,
      title: "Secure Access Controls",
      description: "Multi-factor authentication and role-based access ensure only authorized personnel can access client information."
    },
    {
      icon: Server,
      title: "Secure Infrastructure",
      description: "Our systems are hosted on enterprise-grade, SOC 2 compliant cloud infrastructure with 24/7 monitoring."
    },
    {
      icon: Eye,
      title: "Privacy by Design",
      description: "Data minimization principles ensure we only collect and retain information necessary for service delivery."
    }
  ];

  const complianceStandards = [
    "GDPR (General Data Protection Regulation)",
    "CCPA (California Consumer Privacy Act)",
    "SOC 2 Type II Compliance",
    "ISO 27001 Security Standards",
    "PCI DSS for Payment Processing",
    "UK Data Protection Act 2018"
  ];

  return (
    <>
      <EnhancedSEO
        title="Privacy Policy - Vincent Growth Partners"
        description="Learn how Vincent Growth Partners protects your data with enterprise-grade security, 3+ years cybersecurity expertise, and strict compliance standards."
        keywords="privacy policy, data protection, cybersecurity, GDPR compliance, secure data handling, client privacy"
        url="https://vincentgrowthpartners.com/privacy-policy"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-primary via-primary to-primary-light">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Privacy <span className="text-secondary">Policy</span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
                Your data security is our top priority. Learn how we protect your information 
                with enterprise-grade security and 3+ years of cybersecurity expertise.
              </p>
              <div className="flex items-center justify-center space-x-4 text-secondary">
                <Award className="w-6 h-6" />
                <span className="text-lg font-semibold">3+ Years Cybersecurity Experience</span>
              </div>
            </div>
          </div>
        </section>

        {/* Security Overview */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center text-foreground">
                Enterprise-Grade <span className="text-secondary">Data Security</span>
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {securityMeasures.map((measure, index) => (
                  <Card key={index} className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 shadow-elegant">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mx-auto mb-4">
                        <measure.icon className="w-8 h-8 text-secondary" />
                      </div>
                      <h3 className="font-bold text-foreground mb-3">{measure.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{measure.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Data Collection & Usage */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold mb-12 text-center text-foreground">
                What Data We <span className="text-secondary">Collect & Why</span>
              </h2>
              
              <div className="space-y-8">
                <Card className="border-2 border-secondary/20 shadow-elegant">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-foreground mb-4">Business Information Collection</h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-foreground">Contact & Business Details</p>
                          <p className="text-muted-foreground">Name, email, phone, business address, and industry information to deliver our services effectively.</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-foreground">Ideal Customer Profile (ICP) Data</p>
                          <p className="text-muted-foreground">Target audience demographics, behavioral patterns, and preferences to create personalized marketing strategies.</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-foreground">Strategic Brainstorm Sessions</p>
                          <p className="text-muted-foreground">Business goals, challenges, competitive analysis, and strategic insights gathered during consultation sessions.</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-foreground">Campaign Performance Data</p>
                          <p className="text-muted-foreground">Analytics, conversion rates, and ROI metrics to optimize your marketing campaigns and report on results.</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-secondary/20 shadow-elegant">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-foreground mb-4">Data Retention & Security</h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <Lock className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-foreground">Secure Data Retention</p>
                          <p className="text-muted-foreground">All client data is securely stored and locked down for a minimum of 3 years to ensure continuity of service and compliance with legal requirements.</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Shield className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-foreground">Cybersecurity Expertise</p>
                          <p className="text-muted-foreground">Our team brings 3+ years of specialized cybersecurity experience, ensuring your sensitive business information is protected against threats.</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Eye className="w-6 h-6 text-secondary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-foreground">Controlled Access</p>
                          <p className="text-muted-foreground">Only authorized team members involved in your project have access to your data, with all access logged and monitored.</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Compliance Standards */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold mb-12 text-center text-foreground">
                Compliance <span className="text-secondary">Standards</span>
              </h2>
              
              <Card className="border-2 border-secondary/20 shadow-elegant">
                <CardContent className="p-8">
                  <p className="text-lg text-muted-foreground mb-8 text-center">
                    We maintain strict compliance with international data protection regulations and industry security standards.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {complianceStandards.map((standard, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                        <span className="text-foreground font-medium">{standard}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Your Rights */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold mb-12 text-center text-foreground">
                Your <span className="text-secondary">Data Rights</span>
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 shadow-elegant">
                  <CardContent className="p-6">
                    <Users className="w-12 h-12 text-secondary mb-4" />
                    <h3 className="text-xl font-bold text-foreground mb-3">Access & Control</h3>
                    <p className="text-muted-foreground">You have the right to access, modify, or delete your personal data at any time.</p>
                  </CardContent>
                </Card>
                
                <Card className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 shadow-elegant">
                  <CardContent className="p-6">
                    <FileText className="w-12 h-12 text-secondary mb-4" />
                    <h3 className="text-xl font-bold text-foreground mb-3">Data Portability</h3>
                    <p className="text-muted-foreground">Request a copy of your data in a structured, machine-readable format.</p>
                  </CardContent>
                </Card>
                
                <Card className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 shadow-elegant">
                  <CardContent className="p-6">
                    <Shield className="w-12 h-12 text-secondary mb-4" />
                    <h3 className="text-xl font-bold text-foreground mb-3">Breach Notification</h3>
                    <p className="text-muted-foreground">We commit to notifying you within 72 hours of any potential data security incident.</p>
                  </CardContent>
                </Card>
                
                <Card className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 shadow-elegant">
                  <CardContent className="p-6">
                    <Lock className="w-12 h-12 text-secondary mb-4" />
                    <h3 className="text-xl font-bold text-foreground mb-3">Opt-Out Rights</h3>
                    <p className="text-muted-foreground">Control how your data is used for marketing purposes and communication preferences.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-6 text-foreground">
                Questions About Your <span className="text-secondary">Privacy?</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Our Data Protection Officer is available to address any privacy concerns or data requests.
              </p>
              
              <div className="bg-muted/50 rounded-lg p-8 mb-8">
                <h3 className="text-lg font-semibold text-foreground mb-4">Data Protection Contact</h3>
                <p className="text-muted-foreground mb-2">Email: privacy@vincentgrowthpartners.co.uk</p>
                <p className="text-muted-foreground mb-4">Response Time: Within 48 hours</p>
                <p className="text-sm text-muted-foreground italic">
                  Last Updated: January 2025 | Effective Date: January 1, 2025
                </p>
              </div>
              
               <div className="text-center space-y-4">
                 <Button variant="premium" size="lg" asChild>
                   <a href="mailto:privacy@vincentgrowthpartners.co.uk">
                     Contact Data Protection Officer
                   </a>
                 </Button>
                 <div className="flex justify-center space-x-6 mt-6">
                   <a href="/terms-of-service" className="text-primary hover:text-primary/80 underline font-medium">
                     Terms of Service
                   </a>
                   <a href="/content-policy" className="text-primary hover:text-primary/80 underline font-medium">
                     Content Policy
                   </a>
                 </div>
               </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicy;